<template>
  <div class="attribute-page">
    <ContentBlock style="padding: 0; display: flex; height: 100%;">
      <!-- 左侧：属性分类树 -->
      <div class="left-panel">
        <CategoryManager ref="categoryManager" @category-change="handleCategoryChange" @open-dictionary-info="handleOpenDictionaryInfo" />
      </div>

      <!-- 右侧：属性字典释义 -->
      <div v-if="showDictionaryInfo" class="right-panel dictionary-info-panel" style="flex:1; overflow-y: auto; padding: 20px 24px;">
        <div class="dict-info-title">属性字典释义：</div>
        <div class="dict-info-desc">
          在软件系统中，属性字典（Data Dictionary）是用于统一定义、描述和管理系统内所有数据元素的权威元数据集合。它以结构化的方式，将每个数据项的名称、类型、格式、取值范围、业务含义及关联关系进行标准化编码，确保不同模块、团队与系统间对数据的理解保持一致，请一定要重视属性字典的维护。
        </div>
        <div class="dict-info-rules">
          <p>1、系统内置属性字典由软件厂商维护，用户可以引用，也可以禁用，但不可以修改。</p>
          <p>2、用户自定义属性字典由用户自定义不可与系统内置属性字典名称重复，用户可以根据自身需求进行增删改。</p>
        </div>
      </div>

      <!-- 右侧：属性列表 -->
      <div v-else class="right-panel" style="flex:1; overflow: hidden; padding: 0 10px 84px;">
        <!-- 基本信息 -->
        <div v-if="currentCategory" class="category-info-block">
          <div class="info-block-title">基本信息</div>
          <div class="info-fields">
            <div class="info-item">
              <div class="info-label">属性分类名称</div>
              <div class="info-value">{{ currentCategory.name || '-' }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">分类状态</div>
              <div class="info-value">
                <span>{{ currentCategory.enableFlagDesc || '-' }}</span>
                <span class="category-status-action" @click="handleToggleCategoryStatus">{{ currentCategory.enableFlag ? '禁用' : '启用' }}</span>
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">创建人</div>
              <div class="info-value">{{ currentCategory.createUserName || '-' }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">创建时间</div>
              <div class="info-value">{{ currentCategory.createTime | date-format }}</div>
            </div>
          </div>
        </div>

        <TableWithPagination
          class="special"
          :columnList="columnList"
          :tableData="targetLists"
          :toolBar="toolBar"
          :leftToolbar="tableButtons"
          :sortInfo.sync="sortInfo"
          tableBorder="default"
          :showRightToolbar="true"
          :pagination.sync="pagination"
          @size-change="getTargetLists"
          @current-change="getTargetLists"
          @sort-change="getTargetLists"
          :highHoverRow="false"
          :highlightRow="false"
        />
      </div>
    </ContentBlock>

    <AdvancedSearchComponent ref='JZAdvancedSearchComponent' @updatePageData="updatePageData"></AdvancedSearchComponent>
    <EditAttribute ref='JZEditAttributeComponent' @handleReset="handleReset" />
    <RecycleBin ref='JZRecycleBinComponent' @restored="handleSearch" />
  </div>
</template>

<script>
import TableWithPagination, { TablePaginationMixin } from '@/components/common/TableWithPagination';
import { getAttributeLists, deleteAttribute, enableOrDisableStatus, getGoodsAttributeCount } from '@/api/PlatformSales/ProductAttributes';
import { enableOrDisableAttributeCategory } from '@/api/PlatformSales/AttributeCategory';
import icRefresh from '@/assets/table/ic_refresh.svg';
import icAdvanceSearch from '@/assets/table/ic_advanceSearch.svg';
import AdvancedSearchComponent from './component/AdvancedSearchComponent.vue';
import EditAttribute from './component/EditAttribute.vue';
import CategoryManager from './component/AttributeCategoryManager.vue';
import RecycleBin from './component/RecycleBin.vue';

export default {
  name: 'PlatformSales_ProductAttributes',
  components: {
    TableWithPagination,
    AdvancedSearchComponent,
    EditAttribute,
    CategoryManager,
    RecycleBin,
  },
  mixins: [TablePaginationMixin],
  computed: {
    tableButtons () {
      const hasChildren = !!(this.currentCategory && this.currentCategory.children && this.currentCategory.children.length > 0);
      return [
        {
          name: '新增属性',
          status: 'primary',
          size: 'small',
          code: 'addAttribute',
          icon: 'vxe-icon--plus',
          permission: '',
          disabled: hasChildren,
          click: this.handleAdd,
        },
      ];
    },
    toolBar () {
      return [
        {
          type: 'custom',
          render: () => (
            <el-button size='small' onClick={this.handleOpenRecycleBin}>回收站</el-button>
          ),
        },
        {
          type: 'custom',
          render: () => (
            <el-input placeholder='请输入属性名称' style='width:250px;' v-model={this.form.name}>
              <el-button slot="suffix" icon="el-icon-search" onClick={this.handleSearch} style='position: absolute;height: 100%;right: -5px;top: 0;'></el-button>
            </el-input>
          ),
        },
        {
          type: 'custom',
          render: () => (
            <vxe-button circle onClick={this.handleAdvancedSearch}>
              <img style='font-size:14px;width:15px;height:15px;' src={icAdvanceSearch} />
            </vxe-button>
          ),
        },
        {
          type: 'custom',
          render: () => (
            <vxe-button circle onClick={this.handleReset}>
              <img style='font-size:14px;width:15px;height:15px;' src={icRefresh} />
            </vxe-button>
          ),
        },
      ];
    },
    columnList () {
      return [
        { tooltip: true, label: '属性名', minWidth: '150px', prop: 'name' },
        { tooltip: true, label: '属性值类型', minWidth: '120px', prop: 'valueTypeDesc' },
        { tooltip: true, label: '备注', minWidth: '150px', prop: 'remark' },
        { tooltip: true, label: '创建时间', minWidth: '150px', prop: 'createTime', sortable: true, format: this.$options.filters['date-format'] },
        { tooltip: true, label: '创建人', minWidth: '120px', prop: 'createUserName' },
        { tooltip: true, label: '状态', minWidth: '100px', prop: 'enableFlagDesc' },
        {
          label: '操作',
          prop: 'action',
          width: '180px',
          fixed: 'right',
          render: (h, { row }) => {
            const actions = [
              <span class="table-action" onClick={() => this.handleView(row)}>查看</span>,
              <span class="table-action" onClick={() => this.handleEdit(row)}>编辑</span>,
              <span class="table-action" onClick={() => this.handleChangeStatus(row)}>{row.enableFlag ? '禁用' : '启用'}</span>,
              <span class="table-action table-action-delete" onClick={() => this.handleDelete(row)}>删除</span>,
            ];
            return h('div', actions);
          },
        },
      ];
    },
  },
  data () {
    return {
      form: {
        enableFlag: '',
        name: '',
        attributeCategoryId: null,
      },
      currentCategory: null,
      targetLists: [],
      enableFlagList: [
        { label: '全部', value: '', num: 0 },
        { label: '已启用', value: true, num: 0 },
        { label: '已禁用', value: false, num: 0 },
      ],
      showDictionaryInfo: false,
    };
  },
  methods: {
    handleOpenDictionaryInfo () {
      this.showDictionaryInfo = true;
    },
    handleCategoryChange (category) {
      this.showDictionaryInfo = false;
      this.currentCategory = category;
      this.form.attributeCategoryId = category ? category.id : null;
      this.handleSearch();
    },
    handleClearCategory () {
      this.currentCategory = null;
      this.form.attributeCategoryId = null;
      this.$refs.categoryManager && this.$refs.categoryManager.$refs.categoryTree && this.$refs.categoryManager.$refs.categoryTree.setCurrentKey(null);
      this.handleSearch();
    },
    handleAdd () {
      if (this.currentCategory && this.currentCategory.children && this.currentCategory.children.length > 0) {
        this.$message.warning('当前分类存在子分类，不允许新增属性');
        return;
      }
      this.$refs.JZEditAttributeComponent.JZForm = {
        name: '',
        remark: '',
        valueType: '',
        valueRule: '',
        hasUnitFlag: false,
        unitName: '',
        timePrecision: '',
        isRange: false,
        decimalPlaces: 2,
        attachmentTypeList: [],
        attachmentSize: 10,
        attributeCategoryId: this.form.attributeCategoryId || '',
        valueList: [],
      };
      this.$refs.JZEditAttributeComponent.currentId = null;
      this.$refs.JZEditAttributeComponent.isViewMode = false;
      this.$refs.JZEditAttributeComponent.dialogTitle = '添加属性';
      this.$refs.JZEditAttributeComponent.initPage();
    },
    handleEdit (row) {
      this.$refs.JZEditAttributeComponent.currentId = row.id;
      this.$refs.JZEditAttributeComponent.isViewMode = false;
      this.$refs.JZEditAttributeComponent.dialogTitle = '编辑属性';
      this.$refs.JZEditAttributeComponent.initPage();
    },
    handleChangeStatus (row) {
      const title = row.enableFlag ? '禁用' : '启用';
      this.$confirm(`确认${title}属性[${row.name}]吗？`, title).then(() => {
        enableOrDisableStatus({ id: row.id, enableFlag: !row.enableFlag }).then(() => {
          this.$message.success(`${title}成功`);
          this.handleSearch();
        });
      });
    },
    handleDelete (row) {
      this.$confirm(`确认删除属性[${row.name}]吗？`, '删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        customClass: 'W400',
      }).then(() => {
        deleteAttribute({ id: row.id }).then(() => {
          this.$message.success('删除成功');
          this.handleSearch();
        });
      });
    },
    handleSearch () {
      getGoodsAttributeCount({}).then(({ body }) => {
        this.enableFlagList = [
          { label: '全部', value: '', num: body.enableCount + body.disableCount },
          { label: '已启用', value: true, num: body.enableCount },
          { label: '已禁用', value: false, num: body.disableCount },
        ];
      });
      this.pagination.currentPage = 1;
      this.getTargetLists();
    },
    handleReset () {
      this.form = { ...this.$options.data().form, attributeCategoryId: this.form.attributeCategoryId };
      this.handleSearch();
    },
    getTargetLists () {
      const params = {
        ...this.form,
        pageNum: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      if (this.sortInfo.orderByProperty && this.sortInfo.orderByType) {
        params.orderBy = [this.sortInfo];
      }
      getAttributeLists(params).then(({ body }) => {
        this.targetLists = body?.list || [];
        this.pagination.total = body?.total || 0;
      });
    },
    handleAdvancedSearch () {
      this.$refs.JZAdvancedSearchComponent.JZForm = JSON.parse(JSON.stringify(this.form));
      this.$refs.JZAdvancedSearchComponent.initPage();
    },
    updatePageData (form) {
      this.form = JSON.parse(JSON.stringify(form));
      this.handleSearch();
    },
    handleToggleCategoryStatus () {
      const action = this.currentCategory.enableFlag ? '禁用' : '启用';
      this.$confirm(`确定要${action}分类「${this.currentCategory.name}」吗？`, `${action}分类`, {
        confirmButtonText: action,
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        enableOrDisableAttributeCategory({ id: this.currentCategory.id, enableFlag: !this.currentCategory.enableFlag }).then(() => {
          this.$message.success(`${action}成功`);
          const newEnableFlag = !this.currentCategory.enableFlag;
          this.currentCategory = { ...this.currentCategory, enableFlag: newEnableFlag, enableFlagDesc: newEnableFlag ? '启用' : '禁用' };
          this.$refs.categoryManager.filterEnableFlag = null;
          this.$refs.categoryManager.loadTree();
        });
      });
    },
    handleOpenRecycleBin () {
      this.$refs.JZRecycleBinComponent.initPage(this.form.attributeCategoryId);
    },
    handleView (row) {
      this.$refs.JZEditAttributeComponent.currentId = row.id;
      this.$refs.JZEditAttributeComponent.isViewMode = true;
      this.$refs.JZEditAttributeComponent.dialogTitle = '属性详情';
      this.$refs.JZEditAttributeComponent.initPage();
    },
  },
  created () {
    this.handleReset();
    this.$nextTick(() => {
      this.$route.query.isAddAttribute && this.handleAdd();
    });
  },
};
</script>

<style scoped lang='scss'>
.attribute-page {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.left-panel {
  width: 300px;
  flex-shrink: 0;
  background: #fff;
  overflow-y: auto;
}

.right-panel {
  flex: 1;
  overflow: hidden;
}

/deep/ .topTitleContainer {
  height: 60px;
  background: #FFFFFF;
  box-shadow: inset 0px -1px 0px 0px #DFE2E8;
  border-radius: 6px 6px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 14px;
  color: #606266;
}

.current-category-tag {
  margin-left: 8px;
}

/deep/ .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
  background-color: #409EFF;
  border-color: #409EFF;
}

/deep/.vxe-tools--wrapper {
  display: flex;
  justify-content: space-between;

  &>.el-button { margin-left: 0.5em; }
  &>div.el-input { margin-left: 0.5em; }
  &>.vxe-button { margin-left: 0.5em; }
}

/deep/ .vxe-tools--operate {
  &>.vxe-button { margin-left: 0.5em; }
}

.category-info-block {
  background: #fff;
  border-radius: 4px;
  margin: 10px 0 8px;
  padding: 14px 20px 16px;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.info-block-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding-left: 10px;
  border-left: 3px solid #409EFF;
  line-height: 1;
  margin-bottom: 14px;
}

.info-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 0;
}

.info-item {
  width: 25%;
  min-width: 180px;
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 0;
}

.info-label,
.info-value {
  font-size: 13px;
  color: #303133;
  white-space: nowrap;
}

.info-label {
  &::after {
    content: '：';
  }
}

.category-status-action {
  font-size: 13px;
  color: #409EFF;
  cursor: pointer;
  margin-left: 8px;
  &:hover {
    opacity: 0.8;
  }
}

/deep/ .action {
  color: #409EFF;
  cursor: pointer;
  &:hover { color: rgba(64, 158, 255, 0.7); text-decoration: underline; }
}

.dictionary-info-panel {
  background: #fff;
}

.dict-info-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.dict-info-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
  margin-bottom: 12px;
}

.dict-info-rules {
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
  margin-bottom: 16px;

  p {
    margin: 4px 0;
  }
}
</style>
